import { useState, useEffect, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import api from '../../services/api';

/**
 * ForgotPasswordPage - Página de recuperação de senha
 * 
 * Migrada para Tailwind CSS com fidelidade visual ao original.
 * Layout: basic-layout com header gradiente e fundo cinza.
 */
export function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [flash, setFlash] = useState<{ type: 'success' | 'danger'; message: string } | null>(null);
  const navigate = useNavigate();

  // Adiciona classe basic-layout ao body (layout diferente do login)
  useEffect(() => {
    document.body.className = 'basic-layout';
    // Adiciona background inline como no original
    document.documentElement.style.backgroundColor = '#EDF5F7';
    return () => {
      document.body.className = 'fix-header fix-sidebar page-water home';
      document.documentElement.style.backgroundColor = '';
    };
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setFlash(null);
    setIsSubmitting(true);

    try {
      const response = await api.post('/condomino/recupercao-de-senha', {
        user: { email }
      });
      // Usa a mensagem da API ou fallback
      const message = response.data?.message || t('views.forgot_password.success', 'E-mail de recuperação de senha enviado com sucesso.');
      // Redireciona para login com mensagem de sucesso
      navigate('/login', { state: { flash: { type: 'success', message } } });
    } catch (err: unknown) {
      let errorMessage = t('views.forgot_password.error', 'Erro ao enviar email de recuperação');
      if (typeof err === 'object' && err !== null && 'response' in err) {
        const response = (err as { response?: { data?: { error?: string } } }).response;
        errorMessage = response?.data?.error || errorMessage;
      }
      setFlash({ type: 'danger', message: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-mist-100">
      {/* Header com gradiente */}
      <div className="
        relative
        bg-gradient-to-br from-navy-900 to-navy-700
        h-[190px] max-[812px]:h-[80px]
        mb-[10px]
        py-[20px]
      ">
        {/* Logo centralizado */}
        <div className="text-center">
          <img 
            src="/images/logo_login.png" 
            alt="Laager" 
            className="max-[812px]:h-[55px]"
          />
        </div>
      </div>

      {/* Container principal */}
      <div className="max-w-full px-4">
        {/* Flash message */}
        {flash && (
          <div className="flex justify-center px-[20px] pt-[20px]">
            <div className="
              relative w-full max-w-[540px]
              pl-[20px] pr-10 py-[12px]
              !bg-alert-caution text-white
              rounded-[10px]
            " role="alert">
              <strong className="block font-bold text-[13px]">{t('views.layouts.home.attention', 'Atenção!')}</strong>
              <small className="text-[12px] font-normal">{flash.message}</small>
               <button
                type="button"
                className="absolute top-[10px] right-[12px] border-0 rounded-full text-[18px] leading-none font-normal text-white opacity-70 hover:opacity-100 bg-transparent cursor-pointer"
                onClick={() => setFlash(null)}
                aria-label={t('common.close', 'Fechar')}
              >
                <span aria-hidden="true">×</span>
              </button>
            </div>
          </div>
        )}

        {/* Formulário */}
        <div className="flex justify-center p-[20px]">
          <form
            onSubmit={handleSubmit}
            className="w-full max-w-[540px] text-center bg-white rounded-[16px] shadow-[0_10px_30px_rgba(17,33,51,0.08)] px-[26px] py-[32px]"
            role="form"
          >
            {/* Título */}
            <div className="text-navy-800 text-[17px] font-bold mb-[6px]">
              {t('views.forgot_password.title', 'Esqueci a senha')}
            </div>
            <p className="text-[12px] text-navy-700/70 mb-[22px] leading-[1.5]">
              {t('views.forgot_password.subtitle', 'Informe seu e-mail cadastrado para receber o link de redefinição.')}
            </p>

            {/* Campo email */}
            <div className="mb-5 flex justify-center">
              <input
                type="email"
                className="
                  w-full h-[42px] px-[17px]
                  border border-solid border-mist-300 rounded-[20px]
                  text-[13px] text-navy-800
                  bg-white
                  font-['Poppins',sans-serif]
                  focus:outline-none focus:ring-2 focus:ring-sky-500/30 focus:border-sky-500
                  placeholder:text-mist-500
                "
                placeholder={t('views.forgot_password.email_placeholder', 'Digite seu email para envio de token')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoFocus
                required
              />
            </div>

            {/* Botão enviar */}
            <button
              type="submit"
              className="
                w-full mx-auto block
                py-[11px] px-[20px]
                bg-lime-500 hover:bg-lime-400
                !text-navy-900 text-[13px] font-bold
                border-0 rounded-[20px]
                text-center cursor-pointer
                transition-colors
                disabled:opacity-50 disabled:cursor-not-allowed
              "
              disabled={isSubmitting}
            >
              {isSubmitting ? t('views.registration.sending', 'Enviando...') : t('views.forgot_password.submit', 'Enviar')}
            </button>

            {/* Link voltar */}
            <div className="text-center mt-4">
              <Link
                to="/login"
                className="
                  inline-flex items-center justify-center gap-2 w-full
                  py-[11px] px-[20px]
                  bg-mist-100 hover:bg-mist-300/60
                  !text-navy-800 text-[13px] font-medium
                  border-0 rounded-[20px]
                  transition-colors
                "
              >
                <i className="fa fa-arrow-left" aria-hidden="true"></i> {t('common.back_to_login', 'Voltar para o login')}
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
